/**
 * The alert strip — three permanent counters at the very top of the task panel
 * on every page (the owner, 2026-10-06: "missed calls, stuck calls, and
 * unassigned text messages all have their own permanent alert counter on the
 * top of everyone's apps … so everyone knows there are missed calls").
 *
 *   Missed calls     → the Missed calls pool (unclaimed callbacks)
 *   Stuck calls      → /calls (open calls nobody has moved for 20+ minutes),
 *                      or straight to YOUR stuck call when you have one
 *   Unassigned texts → /texts (open threads nobody has claimed)
 *
 * Calm at zero, red with a pulsing number above it — except Stuck calls,
 * which is red only when one of the stuck calls is the viewer's own (the
 * owner, 2026-10-08: "make it show as red only if it's your stuck call"); a
 * CSR cannot see or clear anybody else's, so for them the company number
 * shows calm. Company-wide numbers, plus the viewer's own share of the stuck
 * calls, from /resources/alert-counts: read on mount, every 20 s while the tab is visible,
 * and the moment the tab becomes visible again (which restarts the 20 s clock,
 * so the two never fire back to back). Never two reads at once: a tick while a
 * read is out is skipped. `refreshAlertCounts()` — the task panel calls it
 * after it changes something — reads at once, fresh (past the server's shared
 * 15 s result), or straight after a read already out. A failed read keeps the
 * last good numbers. Until the first read lands the counters show no number
 * at all, so the server render and the hydration render are identical.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { MISSED_CALLS_DEPARTMENT } from '#app/services/tasks/types.ts'
import { cn } from '#app/utils/misc.tsx'

const POLL_MS = 20_000
const COUNTS_URL = '/resources/alert-counts'
const REFRESH_EVENT = 'hep:alert-counts-refresh'

/** Something just changed (a claim, a completion): re-read the counts now. */
export function refreshAlertCounts(): void {
	window.dispatchEvent(new Event(REFRESH_EVENT))
}

type Counts = {
	missedCalls: number
	stuckCalls: number
	unassignedTexts: number
	/** how many of the stuck calls are mine — Stuck calls is red only above zero */
	myStuckCalls: number
	/** my stuck call that has sat longest (its work item): where the counter goes */
	myStuckCallItemId: number | null
}

type CounterKey = 'missedCalls' | 'stuckCalls' | 'unassignedTexts'

const COUNTERS: Array<{
	key: CounterKey
	label: string
	one: string
	many: string
	to: string
	title: string
}> = [
	{
		key: 'missedCalls',
		label: 'Missed calls',
		one: 'missed call',
		many: 'missed calls',
		to: `/tasks?tab=pool&department=${MISSED_CALLS_DEPARTMENT}`,
		title: 'missed calls nobody has claimed yet — claim one and call them back',
	},
	{
		key: 'stuckCalls',
		label: 'Stuck calls',
		one: 'stuck call',
		many: 'stuck calls',
		to: '/calls',
		title: 'open calls nobody has moved for 20 minutes or more',
	},
	{
		key: 'unassignedTexts',
		label: 'Unassigned texts',
		one: 'unassigned text',
		many: 'unassigned texts',
		to: '/texts',
		title: 'open text conversations nobody has claimed yet',
	},
]

function isCount(v: unknown): v is number {
	return typeof v === 'number' && Number.isInteger(v) && v >= 0
}

/** The counts out of a response body, or null when it is not one. */
function readCounts(body: unknown): Counts | null {
	if (!body || typeof body !== 'object') return null
	const b = body as Record<string, unknown>
	if (!isCount(b.missedCalls) || !isCount(b.stuckCalls)) return null
	if (!isCount(b.unassignedTexts)) return null
	// the viewer's own share: absent or malformed reads as "none of them are
	// mine" — calm, never a false alarm, and never a blanked strip
	const myStuckCalls = isCount(b.myStuckCalls) ? b.myStuckCalls : 0
	const myStuckCallItemId =
		isCount(b.myStuckCallItemId) && b.myStuckCallItemId > 0
			? b.myStuckCallItemId
			: null
	return {
		missedCalls: b.missedCalls,
		stuckCalls: b.stuckCalls,
		unassignedTexts: b.unassignedTexts,
		myStuckCalls,
		myStuckCallItemId,
	}
}

type Look = {
	n: number | null
	hot: boolean
	to: string
	label: string
	title: string
}

/**
 * How one counter shows: its number, red (`hot`) or calm, where it links, and
 * what it says to a screen reader. Every counter is red above zero, except
 * Stuck calls: the company number for everyone, red only when one of the
 * stuck calls is MINE — and then it opens my stuck call, not the list.
 */
function look(c: (typeof COUNTERS)[number], counts: Counts | null): Look {
	const n = counts ? counts[c.key] : null
	const plain = n === null ? c.label : `${n} ${n === 1 ? c.one : c.many}`
	const calm = { n, to: c.to, label: plain, title: c.title }
	if (c.key !== 'stuckCalls') return { ...calm, hot: n !== null && n > 0 }
	const mine = counts?.myStuckCalls ?? 0
	if (mine === 0) return { ...calm, hot: false }
	return {
		n,
		hot: true,
		to:
			counts?.myStuckCallItemId != null
				? `/call/${counts.myStuckCallItemId}`
				: c.to,
		label: `${plain}, ${mine} yours`,
		title:
			mine === 1
				? `${c.title} — one of them is yours: open it`
				: `${c.title} — ${mine} of them are yours: open the one that has waited longest`,
	}
}

export function AlertStrip() {
	const [counts, setCounts] = useState<Counts | null>(null)
	// a read is out; and a fresh one was asked for while it was
	const busy = useRef(false)
	const freshAfter = useRef(false)
	// off the page: a read finishing late asks for nothing more
	const gone = useRef(false)

	const load = useCallback(async (fresh = false): Promise<void> => {
		if (busy.current) {
			// a change made while a read is out may postdate that read
			if (fresh) freshAfter.current = true
			return
		}
		busy.current = true
		try {
			const res = await fetch(fresh ? `${COUNTS_URL}?fresh=1` : COUNTS_URL)
			if (!res.ok) return
			const next = readCounts(await res.json())
			if (next) setCounts(next)
		} catch {
			// keep the last good numbers; the next tick tries again
		} finally {
			busy.current = false
			if (freshAfter.current && !gone.current) {
				freshAfter.current = false
				void load(true)
			}
		}
	}, [])

	useEffect(() => {
		gone.current = false
		let timer: ReturnType<typeof setInterval> | undefined
		const restartClock = () => {
			clearInterval(timer)
			timer = setInterval(() => {
				if (document.visibilityState === 'visible') void load()
			}, POLL_MS)
		}
		void load()
		restartClock()
		const onVisibility = () => {
			if (document.visibilityState !== 'visible') return
			void load()
			restartClock()
		}
		const onRefresh = () => void load(true)
		document.addEventListener('visibilitychange', onVisibility)
		window.addEventListener(REFRESH_EVENT, onRefresh)
		return () => {
			gone.current = true
			clearInterval(timer)
			document.removeEventListener('visibilitychange', onVisibility)
			window.removeEventListener(REFRESH_EVENT, onRefresh)
		}
	}, [load])

	return (
		<nav
			aria-label="Alerts"
			className="grid grid-cols-3 divide-x divide-black/20 border-b border-black/70 dark:divide-white/20"
		>
			{COUNTERS.map((c) => {
				const { n, hot, to, label, title } = look(c, counts)
				return (
					<Link
						key={c.key}
						to={to}
						title={title}
						aria-label={label}
						className={cn(
							'flex min-w-0 flex-col items-center justify-center px-1 py-1.5 text-center leading-tight',
							hot
								? 'bg-hep-red text-white'
								: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
						)}
					>
						<span
							className={cn(
								'text-lg font-black tabular-nums',
								hot && 'motion-safe:animate-pulse',
							)}
						>
							{n ?? '–'}
						</span>
						{/* one line in a ~106px column: mixed case measures ~80px where
						    upper case measured ~101px and wrapped */}
						<span className="max-w-full truncate text-[10px] font-bold">
							{c.label}
						</span>
					</Link>
				)
			})}
		</nav>
	)
}
